import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import MonitorOverviewService from '../../service/MonitorService';
import MonitorOverviewCard from '../../component/monitor/MonitorOverviewCard';
import CustomDropdown, { DropdownOption } from '../../component/CustomDropdown';
import { MonitorOverviewProject, OptionResponse, ClusterOptionResponse } from '../../domain/payload/response/monitor/MonitorOverviewResp';

const AUTO_REFRESH_OPTIONS: DropdownOption[] = [
    { id: 0, name: 'Off' },
    { id: 10000, name: '10 seconds' },
    { id: 300000, name: '5 minutes' },
    { id: 600000, name: '10 minutes' },
];

const MonitorOverview: React.FC = () => {
    const { profile } = useSelector((state: any) => state.auth);
    const token = profile?.data?.token;
    const navigate = useNavigate();

    const [projects, setProjects] = useState<MonitorOverviewProject[]>([]);
    const [loading, setLoading] = useState(true);
    const [isRunning, setIsRunning] = useState<string>('all');
    const [selectedClient, setSelectedClient] = useState<string>('all');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [clientOptions, setClientOptions] = useState<OptionResponse[]>([]);
    const [clusterOptions, setClusterOptions] = useState<ClusterOptionResponse[]>([]);
    const [runningOptions] = useState<OptionResponse[]>([
        { id: 'all', name: 'All' },
        { id: 'running', name: 'Running' },
        { id: 'stopped', name: 'Stopped' },
    ]);
    const [autoRefresh, setAutoRefresh] = useState<number>(
        Number(localStorage.getItem("monitorOverviewAutoRefresh") || 0)
    );
    const [lastRefresh, setLastRefresh] = useState<string>("");
    const [firstLoad, setFirstLoad] = useState<boolean>(false);

    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 9;
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    const [selectedCluster, setSelectedCluster] = useState<string>('');
    const searchTermRef = useRef<string>('');
    const pillsScrollRef = useRef<HTMLDivElement>(null);
    const [showScrollRight, setShowScrollRight] = useState(false);

    const monitorOverviewService = MonitorOverviewService();

    const fetchConfig = async () => {
        try {
            const res = await monitorOverviewService.getConfigOptions(
                token,
                isRunning !== 'all' ? isRunning : undefined,
                selectedClient !== 'all' ? selectedClient : undefined,
                searchTermRef.current || undefined,
            );
            if (res.data) {
                setClientOptions(res.data.clientOptions);
                setClusterOptions(res.data.clusterOptions || []);
            }
        } catch (err) {
            console.error('Failed to fetch config:', err);
        }
    };

    const fetchProjects = useCallback(async () => {
        setLoading(true);
        try {
            const res = await monitorOverviewService.getProjectsOverview(
                token,
                isRunning !== 'all' ? isRunning : undefined,
                selectedClient !== 'all' ? selectedClient : undefined,
                selectedCluster || undefined,
                searchTermRef.current || undefined,
                currentPage,
                pageSize
            );

            console.log('Response:', res);

            if (res && res.data) {
                const projectsData = res.data || [];
                const paginationData = res.pagination;

                console.log('Projects:', projectsData);
                console.log('Pagination:', paginationData);

                setProjects(projectsData);

                if (paginationData) {
                    setTotalRecords(paginationData.totalRecords || 0);
                    setTotalPages(paginationData.totalPage || 1);
                } else {
                    setTotalRecords(projectsData.length);
                    setTotalPages(Math.ceil(projectsData.length / pageSize));
                }
            } else {
                setProjects([]);
                setTotalRecords(0);
                setTotalPages(1);
            }

            const now = new Date();
            const formatted = now.toLocaleString("id-ID", {
                timeZone: "Asia/Jakarta",
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                second: "2-digit",
                hour12: false,
            });
            setLastRefresh(`${formatted.replace(",", "")}`);

        } catch (err) {
            console.error('Failed to fetch projects:', err);
            setProjects([]);
            setTotalRecords(0);
            setTotalPages(1);
        } finally {
            setLoading(false);
            if (!firstLoad) setFirstLoad(true);
        }
    }, [token, isRunning, selectedClient, selectedCluster, currentPage, pageSize, firstLoad]);

    const handleSearchClick = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        searchTermRef.current = searchTerm;
        setCurrentPage(1);
        fetchProjects();
        fetchConfig();
    };

    useEffect(() => {
        if (token && firstLoad) {
            fetchProjects();
            fetchConfig();
        }
    }, [token, isRunning, selectedClient, selectedCluster, currentPage]);

    useEffect(() => {
        if (token) {
            fetchProjects();
            fetchConfig();
        }
    }, [token]);

    useEffect(() => {
        localStorage.setItem("monitorOverviewAutoRefresh", String(autoRefresh));
    }, [autoRefresh]);

    useEffect(() => {
        if (!autoRefresh || !firstLoad) return;
        const interval = setInterval(() => {
            fetchProjects();
        }, autoRefresh);
        return () => clearInterval(interval);
    }, [autoRefresh, fetchProjects, firstLoad]);

    const handleViewDetail = (projectId: string) => {
        navigate(`/project/detail/${projectId}`);
    };

    const handlePageChange = (page: number) => {
        if (page < 1 || page > totalPages) return;
        setCurrentPage(page);
    };

    const handleRunningChange = (value: string) => {
        setIsRunning(value);
        setCurrentPage(1);
    };

    const handleClientChange = (value: string) => {
        setSelectedClient(value);
        setCurrentPage(1);
    };

    const handleClusterChange = (clusterId: string) => {
        setSelectedCluster(clusterId);
        setCurrentPage(1);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
    };

    const totalClusterCount = clusterOptions.reduce((sum, c) => sum + c.count, 0);

    const checkPillsScroll = useCallback(() => {
        const el = pillsScrollRef.current;
        if (el) {
            setShowScrollRight(el.scrollWidth > el.clientWidth + el.scrollLeft + 1);
        }
    }, []);

    useEffect(() => {
        checkPillsScroll();
        window.addEventListener('resize', checkPillsScroll);
        return () => window.removeEventListener('resize', checkPillsScroll);
    }, [clusterOptions, checkPillsScroll]);

    const scrollPillsRight = () => {
        const el = pillsScrollRef.current;
        if (el) {
            el.scrollBy({ left: 200, behavior: 'smooth' });
            setTimeout(checkPillsScroll, 350);
        }
    };

    return (
        <div className="page-wrapper">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="mb-0 fw-bold">Monitor Overview</h5>
            </div>

            <div className="d-flex align-items-center justify-content-between gap-3 mb-4 flex-wrap">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <form
                        className="d-flex align-items-center gap-2"
                        onSubmit={handleSearchClick}
                        style={{ display: 'flex', alignItems: 'center' }}
                    >
                        <input
                            type="text"
                            className="form-control form-control-compact"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            style={{
                                minWidth: 120,
                                height: 38,
                                borderRadius: '6px',
                                border: '1px solid #ced4da'
                            }}
                        />
                        <button
                            type="submit"
                            className="btn btn-primary"
                            style={{ height: 38, padding: '0 16px' }}
                        >
                            <i className="fa-solid fa-search"></i>
                        </button>
                    </form>

                    <div className="d-flex align-items-center gap-2">
                        <label className="fw-semibold text-muted mb-0" style={{ fontSize: '0.8rem' }}>
                            Running
                        </label>
                        <CustomDropdown
                            value={isRunning}
                            options={runningOptions.map(opt => ({ id: opt.id, name: opt.name }))}
                            onChange={(opt) => handleRunningChange(opt.id as string)}
                            buttonStyle={{ minWidth: '130px', height: '38px' }}
                        />
                    </div>

                    <div className="d-flex align-items-center gap-2">
                        <label className="fw-semibold text-muted mb-0" style={{ fontSize: '0.8rem' }}>
                            Client
                        </label>
                        <CustomDropdown
                            value={selectedClient}
                            options={[
                                { id: 'all', name: 'All Clients' },
                                ...clientOptions.map(opt => ({ id: opt.id, name: opt.name })),
                            ]}
                            onChange={(opt) => handleClientChange(opt.id as string)}
                            buttonStyle={{ minWidth: '160px', height: '38px' }}
                        />
                    </div>
                </div>

                <div className="d-flex align-items-center gap-2">
                    <label className="fw-semibold text-muted mb-0">Auto Refresh</label>
                    <CustomDropdown
                        value={autoRefresh}
                        options={AUTO_REFRESH_OPTIONS}
                        onChange={(opt) => setAutoRefresh(Number(opt.id))}
                        buttonStyle={{ height: "38px", minWidth: "140px" }}
                    />
                    <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={fetchProjects}
                        style={{ height: "38px", width: "38px", padding: 0 }}
                    >
                        <i className="fa fa-rotate"></i>
                    </button>
                </div>
            </div>

            {/* Cluster Filter Pills */}
            <div className="d-flex align-items-center gap-2 mb-3">
                <div
                    className="cluster-filter-pills"
                    ref={pillsScrollRef}
                    onScroll={checkPillsScroll}
                >
                    {/* All pill */}
                    <span
                        className={`cluster-pill ${selectedCluster === '' ? 'active' : ''}`}
                        onClick={() => handleClusterChange('')}
                    >
                        All
                        <span className="cluster-pill-count">{totalClusterCount}</span>
                    </span>

                    {/* Per-cluster pills */}
                    {clusterOptions.map((cluster) => (
                        <span
                            key={cluster.id}
                            className={`cluster-pill ${selectedCluster === cluster.id ? 'active' : ''}`}
                            onClick={() => handleClusterChange(cluster.id)}
                        >
                            {cluster.name}
                            <span className="cluster-pill-count">{cluster.count}</span>
                        </span>
                    ))}
                </div>

                {showScrollRight && (
                    <button
                        type="button"
                        className="cluster-pills-scroll-btn"
                        onClick={scrollPillsRight}
                    >
                        <i className="fa-solid fa-chevron-right"></i>
                    </button>
                )}

                {/* "Semua" dropdown */}
                <CustomDropdown
                    value={selectedCluster || 'all'}
                    options={[
                        { id: 'all', name: `All (${clusterOptions.length})`, count: totalClusterCount },
                        ...clusterOptions.map(c => ({ id: c.id, name: c.name, count: c.count })),
                    ]}
                    onChange={(opt) => handleClusterChange(opt.id === 'all' ? '' : opt.id as string)}
                    searchable={true}
                    searchPlaceholder="Cari cluster..."
                    buttonStyle={{
                        height: '34px',
                        borderRadius: '20px',
                        fontSize: '0.78rem',
                        fontWeight: 500,
                        border: '1px solid #e2e8f0',
                        padding: '0 14px',
                        minWidth: 'auto',
                        whiteSpace: 'nowrap',
                    }}
                    dropdownStyle={{
                        minWidth: '220px',
                    }}
                    placeholder={`Semua (${clusterOptions.length})`}
                    caretType="chevron"
                    align="right"
                />
            </div>

            <p className="text-end text-muted" style={{ fontSize: "0.85rem" }}>
                Last refresh at {lastRefresh || "-"}
            </p>

            {loading && (
                <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            )}

            {!loading && projects.length === 0 && (
                <div className="text-center py-5">
                    <i className="fa-regular fa-folder-open text-muted" style={{ fontSize: '3rem' }}></i>
                    <h6 className="mt-3 text-muted">No projects found</h6>
                    <p className="text-muted" style={{ fontSize: '0.9rem' }}>
                        {searchTermRef.current ? `No results found for "${searchTermRef.current}".` : 'Try adjusting your filters or check back later.'}
                    </p>
                </div>
            )}

            {!loading && projects.length > 0 && (
                <>
                    <div className="row g-4">
                        {projects.map((project) => (
                            <div key={project.projectId} className="col-12 col-md-6 col-xl-4">
                                <MonitorOverviewCard
                                    project={project}
                                    onViewDetail={handleViewDetail}
                                />
                            </div>
                        ))}
                    </div>

                    <div className="mt-4 d-flex justify-content-end align-items-center">
                        <span className="me-3" style={{ fontSize: '0.9rem' }}>
                            {`${(currentPage - 1) * pageSize + 1}–${Math.min(currentPage * pageSize, totalRecords)} of ${totalRecords}`}
                        </span>

                        <nav>
                            <ul className="mb-0 pagination">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => handlePageChange(1)}
                                        style={{ border: 0 }}
                                        disabled={currentPage === 1}
                                    >
                                        <i className="fa fa-angle-double-left"></i>
                                    </button>
                                </li>
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => handlePageChange(currentPage - 1)}
                                        style={{ border: 0 }}
                                        disabled={currentPage === 1}
                                    >
                                        <i className="fa fa-angle-left"></i>
                                    </button>
                                </li>
                                <li className="page-item disabled">
                                    <span className="page-link" style={{ border: 0 }}>{currentPage}</span>
                                </li>
                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => handlePageChange(currentPage + 1)}
                                        style={{ border: 0 }}
                                        disabled={currentPage === totalPages}
                                    >
                                        <i className="fa fa-angle-right"></i>
                                    </button>
                                </li>
                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => handlePageChange(totalPages)}
                                        style={{ border: 0 }}
                                        disabled={currentPage === totalPages}
                                    >
                                        <i className="fa fa-angle-double-right"></i>
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    </div>
                </>
            )}
        </div>
    );
};

export default MonitorOverview;